import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Trash2 } from 'lucide-react';
import { motion } from 'motion/react';
import { cn, subtleNeo } from '@vidnight/ui';
import { ADMIN_COLORS, NOTIFICATION_BADGE } from '@/constants/theme';
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  useDeleteAllNotifications,
} from '../hooks';
import { formatRelativeDateTime, replaceMessageDate } from '../utils/relativeDateFormat';
import { localizeNotification } from '../utils/localizeNotification';
import type { Notification } from '../types';

function getTypeLabel(type: Notification['type'], t: (key: string) => string): string {
  switch (type) {
    case 'REPORT_SUBMITTED':
      return t('notifications.types.reportSubmitted');
    case 'REPORT_APPROVED':
      return t('notifications.types.reportApproved');
    case 'REPORT_REJECTED':
      return t('notifications.types.reportRejected');
    case 'MONTHLY_SUMMARY':
      return t('notifications.types.monthlySummary');
    case 'PAYROLL_GENERATED':
      return t('notifications.types.payrollGenerated');
    case 'ACCOUNT_APPROVED':
      return t('notifications.types.accountApproved');
    case 'NEW_MESSAGE':
      return t('notifications.types.newMessage');
    case 'RSS_IMMEDIATE':
      return t('notifications.types.rssImmediate');
    case 'RSS_DAILY_DIGEST':
      return t('notifications.types.rssDailyDigest');
    case 'RSS_WEEKLY_DIGEST':
      return t('notifications.types.rssWeeklyDigest');
    default:
      return type;
  }
}

function getTypeBadgeColor(type: string): string {
  return NOTIFICATION_BADGE[type as keyof typeof NOTIFICATION_BADGE] || NOTIFICATION_BADGE.SYSTEM;
}

type FilterType = 'all' | 'unread' | 'read';

export function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(1);

  const isReadParam = filter === 'unread' ? false : filter === 'read' ? true : undefined;
  const { data, isLoading } = useNotifications({ isRead: isReadParam, page, limit: 20 });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();
  const deleteAll = useDeleteAllNotifications();

  const notifications = data?.data ?? [];
  const pagination = data?.pagination;

  const handleNotificationClick = useCallback(
    (n: Notification) => {
      if (!n.isRead) markAsRead.mutate(n.id);

      try {
        if (n.metadata) {
          const meta = JSON.parse(n.metadata);
          if (n.type === 'NEW_MESSAGE' && meta.conversationId) {
            const scrollTo = meta.firstUnreadMessageId || meta.messageId;
            const url = scrollTo
              ? `/messages/${meta.conversationId}?scrollTo=${scrollTo}`
              : `/messages/${meta.conversationId}`;
            navigate(url);
            // Nudge Stackflow's historySyncPlugin to re-read the URL
            requestAnimationFrame(() => {
              window.dispatchEvent(new PopStateEvent('popstate'));
            });
            return;
          }
          if (['RSS_IMMEDIATE', 'RSS_DAILY_DIGEST', 'RSS_WEEKLY_DIGEST'].includes(n.type)) {
            if (meta.sourceUrl) { window.open(meta.sourceUrl, '_blank'); return; }
            navigate('/feed');
            return;
          }
          if (n.type === 'POST_TAGGED') {
            navigate('/feed');
            return;
          }
          if (
            ['REPORT_SUBMITTED', 'REPORT_APPROVED', 'REPORT_REJECTED'].includes(n.type) &&
            meta.reportId
          ) {
            navigate(`/reports?report=${meta.reportId}`);
            return;
          }
        }
      } catch {
        // metadata not parseable, ignore
      }
    },
    [markAsRead, navigate]
  );

  return (
    <motion.div initial="hidden" animate="visible" variants={subtleNeo.pageContainer} className="mx-auto max-w-3xl space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-2xl font-semibold tracking-wide">{t('notifications.title')}</h1>
          <p className="text-xs sm:text-sm" style={{ color: 'var(--user-page-text)' }}>{t('notifications.description')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => markAllAsRead.mutate()}
            className="flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-2 text-sm hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0.5 active:shadow-none transition-all"
            style={{ color: 'var(--user-page-text)' }}
          >
            <CheckCheck className="h-4 w-4" />
            <span className="hidden sm:inline">{t('notifications.markAllRead')}</span>
          </button>
          <button
            onClick={() => {
              if (window.confirm(t('notifications.confirmDeleteAll'))) {
                deleteAll.mutate();
              }
            }}
            className="flex items-center gap-1.5 rounded-xl border border-white/30 bg-red-500 px-3 py-2 text-sm font-bold text-white hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0.5 active:shadow-none transition-all"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t('notifications.deleteAll')}</span>
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="glass-surface flex gap-2 rounded-3xl border border-white/30 p-1.5" style={{ backgroundColor: ADMIN_COLORS.surface }}>
        {(['all', 'unread', 'read'] as FilterType[]).map((f) => (
          <button
            key={f}
            onClick={() => {
              setFilter(f);
              setPage(1);
            }}
            className={cn(
              'flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all',
              filter === f
                ? 'border border-white/30 bg-white/40 shadow-sm'
                : 'border border-transparent hover:border-white/40'
            )}
            style={{ color: filter === f ? 'var(--user-page-heading)' : 'var(--user-page-text)' }}
          >
            {t(`notifications.filter.${f}`)}
          </button>
        ))}
      </div>

      {/* Notification list */}
      {isLoading ? (
        <div className="py-12 text-center" style={{ color: 'var(--user-page-text)' }}>{t('common.loading')}</div>
      ) : notifications.length === 0 ? (
        <div className="glass-surface flex flex-col items-center gap-3 rounded-3xl border border-white/30 py-16" style={{ backgroundColor: ADMIN_COLORS.surface }}>
          <Bell className="h-10 w-10" style={{ color: ADMIN_COLORS.navy + '30' }} />
          <p className="font-medium" style={{ color: 'var(--user-page-text)' }}>{t('notifications.empty')}</p>
        </div>
      ) : (
        <div className="glass-surface divide-y divide-white/20 overflow-hidden rounded-3xl border border-white/30" style={{ backgroundColor: ADMIN_COLORS.surface }}>
          {notifications.map((n) => {
            const localized = localizeNotification(n, t);
            let messageCount = 0;
            if (n.type === 'NEW_MESSAGE' && n.metadata) {
              try {
                const meta = JSON.parse(n.metadata);
                messageCount = meta.messageCount || 1;
              } catch { /* ignore */ }
            }
            return (
            <div
              key={n.id}
              className={cn(
                'flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-white/20 transition-colors',
                !n.isRead && 'bg-primary-50/30'
              )}
              onClick={() => handleNotificationClick(n)}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      getTypeBadgeColor(n.type)
                    )}
                  >
                    {getTypeLabel(n.type, t)}
                  </span>
                  {messageCount > 1 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                      {messageCount > 99 ? '99' : messageCount}
                    </span>
                  )}
                  <span className="text-xs" style={{ color: 'var(--user-page-text)' }}>
                    {formatRelativeDateTime(n.createdAt)}
                  </span>
                </div>
                <p
                  className={cn(
                    'mt-1 text-sm',
                    !n.isRead && 'font-medium'
                  )}
                  style={{ color: n.isRead ? 'var(--user-page-text)' : 'var(--user-page-heading)' }}
                >
                  {localized.title}
                </p>
                <p className="mt-0.5 text-xs" style={{ color: 'var(--user-page-text)' }}>{replaceMessageDate(localized.message)}</p>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {!n.isRead && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead.mutate(n.id);
                    }}
                    className="rounded-lg border border-white/30 p-1.5 bg-green-200 shadow-sm hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-none transition-all"
                    style={{ color: 'var(--user-page-heading)' }}
                    title={t('notifications.markRead')}
                  >
                    <CheckCheck className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNotification.mutate(n.id);
                  }}
                  className="rounded-lg border border-white/30 p-1.5 bg-white/40 shadow-sm hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-none transition-all"
                  style={{ color: 'var(--user-page-heading)' }}
                  title={t('common.delete')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-white/30 px-3 py-1.5 text-sm disabled:opacity-40 hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0.5 active:shadow-none transition-all"
          >
            {t('common.previous')}
          </button>
          <span className="text-sm" style={{ color: 'var(--user-page-text)' }}>
            {page} / {pagination.pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            disabled={page >= pagination.pages}
            className="rounded-lg border border-white/30 px-3 py-1.5 text-sm disabled:opacity-40 hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0.5 active:shadow-none transition-all"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </motion.div>
  );
}
